import * as React from "react";
import { BarChart3, CalendarCheck, CalendarX2, MapPin, Video } from "lucide-react";

import { apiErrorMessage, getPublicPoll, votePoll } from "@/api/client";
import { PollBallot as PollBallotView, PollVoteChoice, PublicPoll } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { conferenceProviderLabel } from "@/lib/calendarProviders";
import { cn } from "@/lib/utils";

import { noteForRequest } from "../inviteeNote";
import { Card, HeaderCard, PublicFooter } from "../public/PublicChrome";
import { PollBallot } from "./PollBallot";
import "../PublicRollingLinkPage.css";
import { PublicLoadingLoop, useLoadingWindow } from "../public/PublicLoading";

/**
 * The public, no-login poll an invitee reaches at {@code /poll/{slug}} (or the branded
 * {@code /@handle/poll/{name}} alias). Same shell as the other two invitee pages: a banner header,
 * a tinted "what you're voting on" card, then the ballot.
 *
 * <p>Vote counts appear only after this person has voted, and only when the host turned them on —
 * and the anonymous view the page loads first structurally cannot carry them, so there is nothing
 * to leak before the receipt arrives.</p>
 */

const WHEN = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
});

type Load =
  | { status: "loading" }
  | { status: "error"; kind: "not-found" | "unreachable" }
  | { status: "ready"; poll: PublicPoll };

/**
 * The poll's random slug, {@code /poll/{slug}}, which is its only address. The branded
 * {@code /@handle/poll/{name}} alias was removed for the same reason as the ad-hoc one: the slug is
 * the access control, and a title-derived alias beside it was guessable.
 */
export function PublicPollPage({ slug }: { slug: string }) {
  const [load, setLoad] = React.useState<Load>({ status: "loading" });
  const [answers, setAnswers] = React.useState<Record<string, PollVoteChoice>>({});
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showGaps, setShowGaps] = React.useState(false);
  const [receipt, setReceipt] = React.useState<PollBallotView | null>(null);

  const fetchPoll = React.useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const poll = await getPublicPoll(slug);
      setLoad({ status: "ready", poll });
    } catch (e) {
      // A 404 is a definitive answer about the link; anything else means we simply couldn't reach
      // the server, which is transient and worth retrying.
      const notFound =
        typeof e === "object" && e !== null && "status" in e && (e as { status: number }).status === 404;
      setLoad({ status: "error", kind: notFound ? "not-found" : "unreachable" });
    }
  }, [slug]);

  React.useEffect(() => {
    void fetchPoll();
  }, [fetchPoll]);

  const poll = load.status === "ready" ? load.poll : null;
  const answered = poll ? poll.options.filter((o) => answers[o.id]).length : 0;
  const complete = poll != null && poll.options.length > 0 && answered === poll.options.length;

  const submit = async () => {
    if (!poll) return;
    if (!complete) {
      setShowGaps(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Vote against the CANONICAL slug the resolver returned, whether we arrived by that slug or
      // by the /@handle/poll/{name} alias. The alias is already public and resolves the same poll,
      // so carrying the slug back grants nothing the visitor did not already have.
      setReceipt(
        await votePoll(poll.slug, {
          name,
          email,
          note: noteForRequest(note),
          website,
          votes: poll.options.map((o) => ({ option_id: o.id, choice: answers[o.id] })),
        }),
      );
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't record your answers"));
    } finally {
      setSubmitting(false);
    }
  };

  const where = poll
    ? [
        poll.conferencing_provider ? conferenceProviderLabel(poll.conferencing_provider) : null,
        poll.location,
      ].filter(Boolean)
    : [];

  // Held on screen for one full revolution once it appears, and not painted at all
  // if the fetch beats 120ms. The ready and error branches gate on it too: during
  // the hold the fetch has finished, so otherwise both would render at once.
  const showLoading = useLoadingWindow(load.status === "loading");

  return (
    <div className="inv-theme min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto flex max-w-2xl flex-col gap-5">
        {showLoading && (
          <Card>
            <PublicLoadingLoop />
          </Card>
        )}

        {!showLoading && load.status === "error" && (
          <Card className="p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40">
              <CalendarX2 className="h-6 w-6 text-amber-700 dark:text-amber-300" />
            </div>
            {load.kind === "not-found" ? (
              <>
                <h1 className="text-lg font-bold text-foreground">This poll doesn't exist</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ask whoever shared it for a new link.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-lg font-bold text-foreground">We couldn't reach the server</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Your connection may have dropped. The link is probably fine. Give it another go.
                </p>
                <Button className="mt-4" onClick={() => void fetchPoll()}>
                  Try again
                </Button>
              </>
            )}
          </Card>
        )}

        {/* The terminal receipt: their own answers, read-only, with the way back to change them. */}
        {receipt && poll && (
          <>
            <HeaderCard
              name={poll.host_name}
              subtitle="Your answers are in"
              avatarUrl={poll.host_avatar_url}
              bannerUrl={poll.host_banner_url}
              socialLinks={poll.host_social_links}
            />
            <Card className="space-y-4 p-6">
              <div className="text-center">
                <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-teal/20">
                  <CalendarCheck className="h-6 w-6 text-brand-teal" />
                </div>
                <h1 className="text-lg font-bold text-foreground">Thanks, that's recorded</h1>
                <p className="mt-1 text-sm text-foreground">
                  {poll.host_name} will pick a time and email you at {receipt.email}.
                </p>
              </div>
              <PollBallot
                rows={receipt.options}
                answers={Object.fromEntries(
                  receipt.options
                    .filter((o) => o.my_choice)
                    .map((o) => [o.id, o.my_choice as PollVoteChoice]),
                )}
                onAnswer={() => {}}
                showCounts={receipt.counts_visible}
                readOnly
              />
              {/* A control, not a caption: the amend path has to be findable without a line of
                  explanatory text telling you it exists. */}
              <a
                href={`/poll/manage/${receipt.manage_token}`}
                className="inline-flex w-full items-center justify-center rounded-full border border-foreground px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted"
              >
                Change my answers
              </a>
            </Card>
          </>
        )}

        {!showLoading && load.status === "ready" && !receipt && poll && (
          <>
            <HeaderCard
              name={poll.host_name}
              subtitle="Help pick a time"
              avatarUrl={poll.host_avatar_url}
              bannerUrl={poll.host_banner_url}
              socialLinks={poll.host_social_links}
            />

            <Card className="p-6 sm:p-7">
              <h2 className="mb-3 text-sm font-bold text-foreground">What you're voting on</h2>
              <div className="flex items-start gap-3 rounded-xl border border-[#bfe0ef] bg-[#e9f3f7] p-4 dark:border-[#2779a7]/50 dark:bg-[#1e5f83]/20">
                <span className="grid h-9 w-9 flex-none place-items-center rounded-lg border border-[#bfe0ef] bg-card text-[#2779a7] dark:border-[#2779a7]/50 dark:text-[#7cc3e6]">
                  <BarChart3 className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground">{poll.title}</p>
                  <p className="text-sm text-foreground">
                    {poll.duration_minutes} minutes · {poll.options.length} times to choose from
                  </p>
                  {where.length > 0 && (
                    <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground">
                      {poll.conferencing_provider && (
                        <span className="inline-flex items-center gap-1">
                          <Video className="h-3.5 w-3.5" />{" "}
                          {conferenceProviderLabel(poll.conferencing_provider)}
                        </span>
                      )}
                      {poll.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5" /> {poll.location}
                        </span>
                      )}
                    </p>
                  )}
                  {poll.description && (
                    <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{poll.description}</p>
                  )}
                </div>
              </div>
            </Card>

            {poll.status === "CONFIRMED" ? (
              <Card className="space-y-3 p-6">
                <h2 className="text-sm font-bold text-foreground">The time is settled</h2>
                {poll.options
                  .filter((o) => o.id === poll.confirmed_option_id)
                  .map((o) => (
                    <p key={o.id} className="text-sm font-medium text-foreground">
                      {WHEN.format(new Date(o.start_at))}
                    </p>
                  ))}
                <div className="space-y-1">
                  {poll.options
                    .filter((o) => o.id !== poll.confirmed_option_id)
                    .map((o) => (
                      <p key={o.id} className="text-sm text-muted-foreground line-through">
                        {WHEN.format(new Date(o.start_at))}
                      </p>
                    ))}
                </div>
              </Card>
            ) : (
              <Card className="space-y-4 p-6">
                <h2 className="text-sm font-bold text-foreground">
                  {poll.voting_open ? "Which times work for you?" : "Voting has closed"}
                </h2>

                {poll.voting_open && (
                  /* Name and email ABOVE the rows, so nobody fills a long ballot and only then
                     discovers there was a form. */
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor="poll-name">Your name</Label>
                      <Input
                        id="poll-name"
                        value={name}
                        maxLength={120}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="poll-email">Your email</Label>
                      <Input
                        id="poll-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                <PollBallot
                  rows={poll.options}
                  answers={answers}
                  onAnswer={(id, choice) => setAnswers((prev) => ({ ...prev, [id]: choice }))}
                  readOnly={!poll.voting_open}
                  highlightMissing={showGaps}
                />

                {poll.voting_open && (
                  <>
                    <div className="space-y-1">
                      <Label htmlFor="poll-note">Anything to add? (optional)</Label>
                      <textarea
                        id="poll-note"
                        value={note}
                        maxLength={500}
                        rows={2}
                        onChange={(e) => setNote(e.target.value)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
                      />
                    </div>

                    {/* Honeypot: a real person never sees or fills this. */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      className="hidden"
                    />

                    {error && <p className="text-sm font-medium text-brand-coral">{error}</p>}

                    <Button
                      onClick={() => void submit()}
                      disabled={submitting || !name.trim() || !email.trim()}
                      className={cn("w-full")}
                    >
                      {submitting ? "Sending…" : "Send my vote"}
                    </Button>
                  </>
                )}

                {!poll.voting_open && poll.respond_by && (
                  <p className="text-sm text-foreground">
                    Answers closed on {poll.respond_by}, {poll.timezone} time.
                  </p>
                )}
              </Card>
            )}
          </>
        )}

        <PublicFooter />
      </div>
    </div>
  );
}
